import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { COURSE_DISCIPLINES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { AddEditAtpCourseComponent } from '../add-edit-atp/add-edit-atp-course/add-edit-atp-course.component';
import { AddEditAtpComponent } from '../add-edit-atp/add-edit-atp.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ICourseFilter } from 'app/models/filters/courseFilter';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-atp-course',
  templateUrl: './atp-course.component.html',
  styleUrls: ['./atp-course.component.scss']
})
export class AtpCourseComponent {
  public isMobile: boolean = false;
  public uiData = LEAD_BASED_PAINT;
  public loading: boolean = true;
  public pageSize: number = 50;  
  public dataSource: MatTableDataSource<any>;
  public courses: any[] = [];
  public filteredCourses: any[] = [];
  public atpList: any[] = [];
  public filteredAtpList: any[] = [];  

  public filterString: string = '';
  public filterATPString: any = '';  
  public delinquents: boolean = true;
  public preApproval: boolean = false;
  public excludeCancelled: boolean = false;
  public courseDiscipline: string = '';
  public startDate = null;
  public endDate = null;

  // Cache the discipline values to avoid creating new array on each change detection
  // These filters use "Contains" logic - e.g., "Inspector" will show "Inspector" and "Inspector/Supervisor" courses
  public readonly courseDisciplineValues = [
    { value: '', displayName: 'All Disciplines' },
    { value: COURSE_DISCIPLINES.inspector, displayName: 'Inspector' },
    { value: COURSE_DISCIPLINES.supervisor, displayName: 'Supervisor' },
    { value: COURSE_DISCIPLINES.worker, displayName: 'Worker' },
    { value: COURSE_DISCIPLINES.riskAssessor, displayName: 'Risk Assessor' },
    { value: COURSE_DISCIPLINES.projectDesigner, displayName: 'Project Designer' },
    { value: COURSE_DISCIPLINES.renovator, displayName: 'Renovator' },
    { value: COURSE_DISCIPLINES.dustSamplingTechnician, displayName: 'Dust Sampling Technician' },
  ];

  public courseFilter: ICourseFilter = {    
    filterCourseString: null,
    filterATPString: null,         
    filterStartDate: null,
    filterEndDate: null,
    filterDelinquents: true,
    filterPreApproval: false,
    filterATPId: null,
    filterDateType: null,
    filterExcludeCancelled: false,
    filterDiscipline: '',
  };
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;
  
  courseDataFields = {    
    layout:{
      columns:['course', 'atpName', 'startDateTime', 'location','receivedPostNoteDate','payment','students','elapsedDays'],
      container:[
        {displayName:'Course',columnName:'course', type:'string', size:'12'},        
        {displayName:'ATP',columnName:'atpName', type:'string', size:'20'},        
        {displayName:'Course Start/End',columnName:'startDateTime', type:'dateTime', size:'10'}, 
        {displayName:'Location',columnName:'location', type:'string', size:'20'}, 
        {displayName:'Post Training Notification Date',columnName:'receivedPostNoteDate', type:'date', size:'10'}, 
        {displayName:'Payment',columnName:'payment', type:'string', size:'13'}, 
        {displayName:'Students',columnName:'students', type:'string', size:'7'}, 
        {displayName:'Days Since Course Ended',columnName:'elapsedDays', type:'string', size:'8'}, 
      ],        
    }
  }  

  private readonly debounceString: Subject<string> = new Subject<string>();
  
  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public sharedService: SharedService,
    public dialog: MatDialog,
    public commonService: CommonDataService,
    public atpService: AtpService,    
    public toastr: ToastrService) 
  {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe(result => {
      this.isMobile = result.matches;
    }); 
    this.initializeDebounceFunction();
    this.loadFilters();
    this.refreshCourses();
    this.loadATPList();
  }

  exportToExcel() {
    const exportData = [];
      
    this.filteredCourses.forEach(course => {                 
      const row = {
        Course: course.course,     
        ATP: course.atpName,            
        'Course Start Date': course.startDateTime,
        Location: course.location,
        'Post Notification Date': course.receivedPostNoteDate,
        Payment: course.payment,
        Students: course.students,
        'Days Since Course Ended': course.elapsedDays,
      };          
      exportData.push(row);
    });
      
    exportData.sort((a, b) => a.ATP.localeCompare(b.ATP));
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      
    ws['!cols'] = [
      { wpx: 200 },
      { wpx: 150 },
      { wpx: 150 },
      { wpx: 250 },
      { wpx: 150 },
      { wpx: 150 },
      { wpx: 100 },
      { wpx: 100 },
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.filterString !== '' ? this.filterString.replace(/\s+/g, '') + '.xlsx' : 'exportedATPCourseRecords.xlsx');
  }

  initializeDebounceFunction(): void {
    this.debounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {           
      this.filterATP(value.trim().toLowerCase());     
    });
  }

  loadFilters(): void {
    this.filterString = this.atpService.filterCourseString;
    this.filterATPString = this.atpService.filterCourseATPString;        
    this.startDate = this.atpService.filterCourseStartDate;
    this.endDate = this.atpService.filterCourseEndDate;
    this.delinquents = this.atpService.filterCourseDelinquents;
    this.preApproval = this.atpService.filterCoursePreApproval;
    this.excludeCancelled = this.atpService.filterCourseExcludeCancelled;
    this.courseDiscipline = this.atpService.filterCourseDiscipline;
    
    this.courseFilter.filterCourseString = this.filterString;
    this.courseFilter.filterATPString = this.filterATPString;
    this.courseFilter.filterStartDate = this.startDate?.toISOString();
    this.courseFilter.filterEndDate = this.endDate?.toISOString();
    this.courseFilter.filterDelinquents = this.delinquents;
    this.courseFilter.filterPreApproval = this.preApproval;
    this.courseFilter.filterExcludeCancelled = this.excludeCancelled;
    this.courseFilter.filterDiscipline = this.courseDiscipline;

  }

  calculateElapsedDays(date: Date): number {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = now.getTime() - targetDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  refreshCourses(){    
    this.loading = true;
    this.atpService.getAllATPCourses(this.courseFilter).subscribe(result => {         
      let remap = result.map(data => ({
        ...data,
        course: data?.id + ': ' + (data?.accreditationDiscipline ?? 'No Discipline') + (data?.accreditationCourseFormat ? '\n' + data.accreditationCourseFormat : '' ) + (data?.accreditationCourseLanguage ? '\n' + data.accreditationCourseLanguage : '') + (data?.stage ? '\n' + data.stage : ''),
        atpName: data?.atp?.name,
        startDateTime: data?.courseStart,        
        location: ' \n' + (data?.atpCourseAddress?.locationName?.length ? data.atpCourseAddress.locationName + '\n' : '') + (data?.atpCourseAddress?.address?.street?.length ? data.atpCourseAddress.address.street + '\n' : '') + (data?.atpCourseAddress?.address?.city?.length ? data.atpCourseAddress.address.city + ', ' : '') + data?.atpCourseAddress?.address?.state + ' ' + data?.atpCourseAddress?.address?.zip + '\n ',
        payment: (data?.accreditationDiscipline === COURSE_DISCIPLINES.renovator || data?.accreditationDiscipline === COURSE_DISCIPLINES.renovatorHands || data?.accreditationDiscipline === COURSE_DISCIPLINES.dustSamplingTechnician)  ? ('$' + (data?.receivedAmount ?? '0') + '\nControl# ' + (data?.controlNumber ?? '')) : 'N/A',
        elapsedDays: data?.courseEnd ? this.calculateElapsedDays(new Date(data?.courseEnd)) : this.calculateElapsedDays(new Date(data?.courseStart)),
      }));      
      this.courses = remap;
      this.filterTable();      
      this.loading = false;
    });
  }

  loadATPList(){
    this.atpService.getATPs().subscribe(result=>{
      this.atpList = result.map(atp=>{return {name: atp.name, id: atp.id}});
      this.atpList.sort((a, b) => a.name.localeCompare(b.name));
      this.filteredAtpList = this.atpList;      
    },error=>{this.toastr.error("Error loading ATPs");})
  }
  
  updateDataSource(){
    this.dataSource = new MatTableDataSource<any>(this.filteredCourses);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.sort.active = 'elapsedDays';
    this.sort.direction = 'asc';   
    this.sort.sortChange.emit();
    this.applySubFilter({target: {value: this.filterString}});
  }

  applySubFilter(event: any): void {    
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if(this.dataSource){
      this.dataSource.filter = filterValue;
      this.dataSource.paginator.firstPage();
    }
  }

  editCourse(row: any){    
    const dialogRef = this.dialog.open(AddEditAtpCourseComponent, {
      width: '90%',
      data: {course: null, atp: row.atp, courseId: row?.id},});
    dialogRef.afterClosed().subscribe(result => {  
      this.refreshCourses();    
    });
  }

  editATP(row: any){    
    const dialogRef = this.dialog.open(AddEditAtpComponent, {
      width: '90%',
      data: {atpId: row.id},});
    dialogRef.afterClosed().subscribe(result => {     
      this.refreshCourses();    
    });
  }

  filterTable() {     
    this.saveFilters();
    this.filteredCourses = this.courses;    
    this.updateDataSource();
  }

  saveFilters(){
    this.atpService.filterCourseString = this.filterString;
    this.atpService.filterCourseATPString = this.filterATPString;
    this.atpService.filterCourseStartDate = this.startDate;
    this.atpService.filterCourseEndDate = this.endDate;
    this.atpService.filterCourseDelinquents = this.delinquents;
    this.atpService.filterCoursePreApproval = this.preApproval;
    this.atpService.filterCourseExcludeCancelled = this.excludeCancelled;
    this.atpService.filterCourseDiscipline = this.courseDiscipline;

    this.courseFilter.filterCourseString = this.filterString;
    this.courseFilter.filterATPString = this.filterATPString;
    this.courseFilter.filterStartDate = this.startDate?.toISOString();
    this.courseFilter.filterEndDate = this.endDate?.toISOString();
    this.courseFilter.filterDelinquents = this.delinquents;
    this.courseFilter.filterPreApproval = this.preApproval;
    this.courseFilter.filterExcludeCancelled = this.excludeCancelled;
    this.courseFilter.filterDiscipline = this.courseDiscipline;
  }

  clearFilters(){
    this.filterString = '';
    this.filterATPString = '';        
    this.startDate = null;
    this.endDate = null;
    this.delinquents = true;
    this.preApproval = false;
    this.excludeCancelled = false;
    this.courseDiscipline = '';
    
    this.filterTable();
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }

  formatDate(date: any){
    let dateString = date.split('-');
    return dateString[1] + '/' + dateString[2] + '/' + dateString[0];
  }

  filterATP(value: any) {
    if (value.length < 1) {
      this.resetATPList();      
    }
    else{      
      this.filteredAtpList = this.atpList.filter(atp => atp.name.toLowerCase().includes(value) || atp.id.toString().includes(value));
    }
  }

  updateATPFilter(): void{
    this.filterATPString = this.filterATPString?.name ?? this.filterATPString;
    this.saveFilters();
  }

  updateDebounceString(event: any){        
    this.debounceString.next(event.target.value);     
  }

  resetATPList(): void {    
    this.filteredAtpList = this.atpList
  }

  displayATP(atp: any): string {
    if (atp) {
        if (atp.id === 0) {
            return atp?.name ?? atp;
        } else {
            return `${atp?.id ? atp.id + ': ' + atp.name : atp}`;
        }
    } else {
        return '';
    }
  }

  displayDateTime(course: any){
    try{      
      let result = this.formatDate(course.courseStart.split('T')[0]) + ' ' + course.courseStart.split('T')[1]
      let end = this.formatDate(course.courseEnd?.split('T')[0]) + ' ' + course.courseEnd?.split('T')[1];      
      if(end === undefined || end === null || end === 'null' || end === '01/01/0001\n00:00:00'){ 
        result = result + '\n' + result;
      }
      else{
        result = result + '\n' + end;
      }
      return result
    }catch{return ''}
  }

  search(){
    this.saveFilters();
    this.refreshCourses();
  }
}